import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeSlashIcon,
  BellIcon,
} from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import SkeletonRows from "../../components/SkeletonRows";
import { ErrorBanner, INPUT_CLS, PrimaryButton } from "../../components/crm/Form";
import SeverityDot from "../../components/monitoring/SeverityDot";
import AlertStatusBadge from "../../components/monitoring/AlertStatusBadge";
import LogAlertModal from "../../components/monitoring/LogAlertModal";
import { useAuth } from "../../contexts/AuthContext";
import { formatApiError } from "../../lib/api";
import {
  ALERT_TYPE_LABEL,
  STATUSES,
  SEVERITIES,
  VENDORS,
  VENDOR_LABEL,
  acknowledgeAlert,
  getMonitoringStats,
  ignoreAlert,
  listAlerts,
  relativeTime,
  resolveAlert,
} from "../../lib/monitoring";

const PAGE_SIZE = 50;

const STATUS_FILTERS = [
  { value: "", label: "All" },
  ...STATUSES,
];

const SEVERITY_FILTERS = [
  { value: "", label: "All" },
  ...SEVERITIES,
];

const VENDOR_FILTERS = [
  { value: "", label: "All" },
  ...VENDORS,
];

export default function MonitoringList() {
  const { user } = useAuth();
  const isOffice = user?.role === "owner" || user?.role === "ops";

  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const status = params.get("status") || "";
  const severity = params.get("severity") || "";
  const vendor = params.get("vendor") || "";
  const search = params.get("search") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [searchInput, setSearchInput] = useState(search);
  const [logOpen, setLogOpen] = useState(false);

  const queryParams = useMemo(() => {
    const q = { page, page_size: PAGE_SIZE };
    if (status) q.status = status;
    if (severity) q.severity = severity;
    if (vendor) q.vendor = vendor;
    if (search) q.search = search;
    return q;
  }, [page, status, severity, vendor, search]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await listAlerts(queryParams);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const loadStats = useCallback(async () => {
    if (!isOffice) return;
    try {
      const s = await getMonitoringStats();
      setStats(s);
    } catch {
      setStats(null);
    }
  }, [isOffice]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Phase 15 — auto-open Log Alert modal when navigated with ?new=alert.
  useEffect(() => {
    if (params.get("new") === "alert" && isOffice) {
      setLogOpen(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, isOffice, setParams]);

  // search debounce
  useEffect(() => {
    setSearchInput(search);
  }, [search]);
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput === search) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("search", searchInput);
      else next.delete("search");
      next.set("page", "1");
      setParams(next);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    next.set("page", "1");
    setParams(next);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refresh = async () => {
    await Promise.all([load(), loadStats()]);
  };

  const onAck = async (id, e) => {
    e.stopPropagation();
    try {
      await acknowledgeAlert(id);
      toast.success("Acknowledged");
      refresh();
    } catch (err2) {
      toast.error(formatApiError(err2));
    }
  };
  const onResolve = async (id, e) => {
    e.stopPropagation();
    try {
      await resolveAlert(id);
      toast.success("Resolved");
      refresh();
    } catch (err2) {
      toast.error(formatApiError(err2));
    }
  };
  const onIgnore = async (id, e) => {
    e.stopPropagation();
    try {
      await ignoreAlert(id);
      toast.success("Ignored");
      refresh();
    } catch (err2) {
      toast.error(formatApiError(err2));
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title="Monitoring Alerts"
          subtitle="Vendor + manual alerts, automation, and triage"
          actions={
            isOffice && (
              <PrimaryButton
                onClick={() => setLogOpen(true)}
                data-testid="monitoring-log-btn"
              >
                <PlusIcon size={16} className="mr-1.5" /> Log alert
              </PrimaryButton>
            )
          }
        />

        <ErrorBanner message={err} />

        {isOffice && stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile
              label="New"
              value={stats.total_new}
              tone="text-slate-900"
              testid="monitoring-stat-new"
              onClick={() => setFilter("status", "new")}
            />
            <StatTile
              label="Critical"
              value={stats.total_critical}
              tone={stats.total_critical > 0 ? "text-red-700" : "text-slate-900"}
              testid="monitoring-stat-critical"
              onClick={() => setFilter("severity", "critical")}
            />
            <StatTile
              label="High"
              value={stats.total_high}
              tone={stats.total_high > 0 ? "text-orange-700" : "text-slate-900"}
              testid="monitoring-stat-high"
              onClick={() => setFilter("severity", "high")}
            />
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search title or description…"
            data-testid="monitoring-search"
            className={INPUT_CLS}
          />
          <ChipRow
            label="Status"
            options={STATUS_FILTERS}
            value={status}
            onChange={(v) => setFilter("status", v)}
            testidPrefix="monitoring-status-filter"
          />
          <ChipRow
            label="Severity"
            options={SEVERITY_FILTERS}
            value={severity}
            onChange={(v) => setFilter("severity", v)}
            testidPrefix="monitoring-severity-filter"
          />
          <ChipRow
            label="Vendor"
            options={VENDOR_FILTERS}
            value={vendor}
            onChange={(v) => setFilter("vendor", v)}
            testidPrefix="monitoring-vendor-filter"
          />
          <div className="text-[11px] text-slate-500 pt-1">
            Showing {items.length} of {total}
            {totalPages > 1 ? ` · page ${page} / ${totalPages}` : ""}
          </div>
        </div>

        <div
          data-testid="monitoring-list"
          className="bg-white border border-slate-200 rounded-md overflow-hidden"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-2.5 w-32">Severity</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Title</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Property</th>
                <th className="px-4 py-2.5 w-24">Vendor</th>
                <th className="px-4 py-2.5 w-32">Status</th>
                <th className="px-4 py-2.5 w-24">Created</th>
                <th className="px-4 py-2.5 w-44 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && <SkeletonRows count={5} cols={9} />}
              {!loading && items.length === 0 && (
                <EmptyState
                  inTable
                  colSpan={9}
                  icon={BellIcon}
                  title={
                    status || severity || vendor || search
                      ? "No alerts match these filters"
                      : "No alerts yet"
                  }
                  description={
                    status || severity || vendor || search
                      ? "Try clearing the filters above to see all alerts."
                      : "Alerts will appear here when monitoring auto-ingests them, or when ops logs one manually."
                  }
                  cta={
                    isOffice && !(status || severity || vendor || search)
                      ? {
                          label: "Log Alert",
                          onClick: () => setLogOpen(true),
                          testId: "monitoring-empty-cta",
                        }
                      : null
                  }
                  testId="monitoring-empty"
                />
              )}
              {items.map((a) => (
                <tr
                  key={a.id}
                  data-testid={`monitoring-row-${a.id}`}
                  onClick={() => navigate(`/monitoring/${a.id}`)}
                  className="border-t border-slate-200 hover:bg-slate-50/70 cursor-pointer"
                >
                  <td className="px-4 py-2.5 align-middle">
                    <SeverityDot severity={a.severity} label={a.severity} />
                  </td>
                  <td className="px-4 py-2.5 align-middle text-xs text-slate-700">
                    {ALERT_TYPE_LABEL[a.alert_type] || a.alert_type}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-sm font-semibold text-slate-900">
                    {a.title}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-xs text-slate-700">
                    {a.customer_name || "—"}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-xs text-slate-700">
                    {a.property_address || "—"}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-xs text-slate-700">
                    {VENDOR_LABEL[a.vendor] || a.vendor}
                  </td>
                  <td className="px-4 py-2.5 align-middle">
                    <AlertStatusBadge status={a.status} />
                  </td>
                  <td className="px-4 py-2.5 align-middle text-xs text-slate-500" title={a.created_at}>
                    {relativeTime(a.created_at)}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-right whitespace-nowrap">
                    {a.status === "new" && (
                      <RowAction
                        label="Acknowledge"
                        icon={<CheckCircleIcon size={14} />}
                        onClick={(e) => onAck(a.id, e)}
                        testid={`monitoring-row-ack-${a.id}`}
                      />
                    )}
                    {(a.status === "acknowledged" || a.status === "auto_ticketed") && (
                      <RowAction
                        label="Resolve"
                        icon={<CheckCircleIcon size={14} />}
                        onClick={(e) => onResolve(a.id, e)}
                        testid={`monitoring-row-resolve-${a.id}`}
                      />
                    )}
                    {(a.status === "new" || a.status === "acknowledged") && (
                      <RowAction
                        label="Ignore"
                        icon={<EyeSlashIcon size={14} />}
                        onClick={(e) => onIgnore(a.id, e)}
                        testid={`monitoring-row-ignore-${a.id}`}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set("page", String(page - 1));
                setParams(next);
              }}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-50"
            >
              Previous
            </button>
            <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set("page", String(page + 1));
                setParams(next);
              }}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <LogAlertModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onCreated={(created) => {
          setLogOpen(false);
          refresh();
          if (created?.id) navigate(`/monitoring/${created.id}`);
        }}
      />
    </Layout>
  );
}

function StatTile({ label, value, tone, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="bg-white border border-slate-200 rounded-md p-4 text-left hover:border-amber-400 hover:shadow-sm transition"
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </div>
      <div className={`text-3xl font-bold mt-1 ${tone}`}>{value}</div>
    </button>
  );
}

function RowAction({ label, icon, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="ml-1 inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-slate-400 hover:text-slate-900 transition"
    >
      {icon} {label}
    </button>
  );
}

function ChipRow({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1 w-16">
        {label}
      </span>
      {options.map((o) => {
        const isActive = value === o.value || (!value && o.value === "");
        return (
          <button
            key={o.value || "_all"}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`${testidPrefix}-${o.value || "all"}`}
            className={`text-xs px-2.5 py-1 rounded-md border transition ${
              isActive
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SkeletonRowsLegacy() {
  // Phase 15 — superseded by shared SkeletonRows. Kept for reference; not
  // mounted anywhere. Safe to delete.
  return null;
}
void SkeletonRowsLegacy;

// Unused import suppression — kept for future "X icon" usage.
void XCircleIcon;
